import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import postPoseBrowseSearch from '@cartwhl/common/api/postPoseBrowseSearch';
import { BrowseResultItem, CategoryAggregation } from '@cartwhl/common/types';

import { noticesStore } from 'stores';

const URL_EXPIRATION_TIME = 600; // 10 minutes in seconds

const BROWSE_POSE_RESULTS_FETCH_SIZE = 500;

// Constants for different horizontal page sizes
const BROWSE_VIEWPORT_GRID_SIZES = {
  LARGE_VIEW: 35,
  MEDIUM_VIEW: 30,
  DEFAULT: 32,
} as const;

// Get the appropriate page size
export function getBrowseViewPageSize(columns: number): number {
  switch (columns) {
    case 7:
      return BROWSE_VIEWPORT_GRID_SIZES.LARGE_VIEW;
    case 3:
    case 5:
    case 6:
      return BROWSE_VIEWPORT_GRID_SIZES.MEDIUM_VIEW;
    default:
      return BROWSE_VIEWPORT_GRID_SIZES.DEFAULT;
  }
}

type CachedBvhUrl = {
  url: string;
  fetchedAt: number;
};

type BrowsePosePage = {
  items: BrowseResultItem[];
  lastSort?: [number, string];
};

type BrowsePoseStoreState = {
  availableCategoryInformation: CategoryAggregation[];
  bvhUrlCache: Record<string, CachedBvhUrl>;
  currentCategory: string;
  currentSearchTerm: string;
  currentSubcategory: string;
  favoritePages: BrowsePosePage[];
  favoriteRecords: BrowseResultItem[];
  hasMore: boolean;
  loading: {
    animation: boolean;
    data: boolean;
    init: boolean;
    nextPage: boolean;
  };
  pages: BrowsePosePage[];
  total: number;
  reviewMode: boolean;
  selectedView: 'library' | 'favorites';
  suggestions: {
    searchTerm: string;
    data: PoseSuggestion[];
    loading: boolean;
    total: number;
  };
};

export const state = proxy<BrowsePoseStoreState>({
  availableCategoryInformation: [], // TODO: Add category information for poses.
  bvhUrlCache: {},
  currentCategory: '',
  currentSearchTerm: '',
  currentSubcategory: '',
  favoritePages: [],
  favoriteRecords: [],
  hasMore: false,
  loading: {
    animation: false,
    data: false,
    init: false,
    nextPage: false,
  },
  pages: [],
  total: 0,
  reviewMode: false,
  selectedView: 'library',
  suggestions: {
    searchTerm: '',
    data: [],
    loading: false,
    total: 0,
  },
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'BrowsePose', enabled: true });
}

export type PoseSuggestion = {
  jobID: string;
  title: string;
  thumbnailUrl?: string;
};

function isUrlExpired(fetchedAt: number): boolean {
  const now = Math.floor(Date.now() / 1000); // Convert to seconds
  return now - fetchedAt > URL_EXPIRATION_TIME;
}

export async function setLoading(key: 'data' | 'animation', loading: boolean) {
  state.loading[key] = loading;
}

export async function initSearchSuggestions(
  params: {
    searchTerm: string;
  },
  signal?: AbortSignal,
) {
  state.suggestions.loading = true;
  state.suggestions.data = [];
  state.suggestions.searchTerm = params.searchTerm;

  try {
    const { data, error } = await postPoseBrowseSearch(
      {
        pageSize: BROWSE_POSE_RESULTS_FETCH_SIZE,
        ...params,
      },
      signal,
    );

    if (error) {
      throw error;
    }

    if (data) {
      const items = Array.isArray(data) ? data : data.items || [];
      const total = data.total || (Array.isArray(data) ? data.length : 0);

      state.suggestions.data = items.slice(0, 20);
      state.suggestions.total = total;
    }
  } catch (error) {
    state.suggestions.data = [];
    state.suggestions.total = 0;
  } finally {
    state.suggestions.loading = false;
  }
}

/**
 * This is needed to be called outside of the debounced search function,
 * otherwise you would see a flash of the previous results
 */
export function prepareLoadingState() {
  state.suggestions.loading = true;
  state.suggestions.data = [];
  state.suggestions.searchTerm = '';
}

export async function initSearch(
  params: {
    searchTerm: string;
    category: string;
    subcategory: string;
  },
  signal?: AbortSignal,
) {
  if (state.selectedView === 'favorites') {
    await fetchFavorites(true);
    return;
  }
  state.loading.data = true;
  state.pages = [];
  state.currentCategory = params.category;
  state.currentSubcategory = params.subcategory;

  try {
    const { data, error } = await postPoseBrowseSearch(
      {
        pageSize: BROWSE_POSE_RESULTS_FETCH_SIZE,
        ...params,
        // review: state.reviewMode,
      },
      signal,
    );

    if (error) {
      throw error;
    }

    if (data) {
      if (
        params.searchTerm &&
        params.category === '' &&
        params.subcategory === '' &&
        data.items.length > 0
      ) {
        state.availableCategoryInformation = data.categoryInformation;
      } else {
        state.availableCategoryInformation = []; // TODO: Add category information for poses.
      }

      state.pages = [
        {
          items: data.items,
          lastSort: data.lastSort,
        },
      ];

      state.hasMore = data.hasMore ?? false;
      state.total = data.total ?? 0;
    }
  } catch (error) {
    if ((error as Error).message.includes('aborted')) {
      console.log('Browse search request was aborted by user');
    } else {
      console.error(`Failed to load browse search results: ${error}`);
      noticesStore.error('Failed to load results');
    }
  } finally {
    state.loading.data = false;
  }
}

export async function loadNextPage() {
  if (!state.hasMore || state.loading.nextPage || state.loading.data) return;
  state.loading.nextPage = true;
  const lastPage = state.pages[state.pages.length - 1];

  try {
    const { data, error } = await postPoseBrowseSearch({
      pageSize: BROWSE_POSE_RESULTS_FETCH_SIZE,
      searchTerm: state.currentSearchTerm,
      category: state.currentCategory,
      subcategory: state.currentSubcategory,
      searchAfter: lastPage?.lastSort,
      review: state.reviewMode,
    });

    if (error) {
      noticesStore.error('Failed to load more results');
      return;
    }

    if (data) {
      state.pages.push({
        items: data.items,
        lastSort: data.lastSort,
      });
      state.hasMore = data.hasMore ?? false;
      state.total = data.total ?? 0;
    }
  } catch (error) {
    noticesStore.error('Failed to load more results');
  } finally {
    state.loading.nextPage = false;
  }
}

export async function fetchBvhFromJob(jobID: string) {
  throw new Error('fetchBvhFromJob for pose library not yet implemented');
  // Here's what the motion library one did.
  // const cachedUrl = getCachedUrl(jobID);
  // if (cachedUrl) return cachedUrl;

  // state.loading.animation = true;

  // const { data, error } = await getJob(jobID);

  // if (error) {
  //   const params = encodeURIComponent(encodeURIComponent(`jobID=${jobID}`));
  //   await handleGenericErrors(error, { redirectTo: `studio/home?${params}` });
  //   state.loading.animation = false;
  // } else if (data) {
  //   const variation = data.variationInfoByID[0];
  //   if (variation.bvhURL) {
  //     cacheUrl(jobID, variation.bvhURL);
  //     return variation.bvhURL;
  //   } else {
  //     noticesStore.error(`Pose could not be found. Please try again`);
  //   }
  // }
  // state.loading.animation = false;
}

export async function fetchFavorites(skipJobFetch: boolean) {
  try {
    // TODO: Implement favorites for poses.
    throw new Error('fetchFavorites for pose library not yet implemented');
  } catch (error) {
    noticesStore.error('Failed to load favorites');
  } finally {
    state.loading.data = false;
  }
}

export function setCurrentSearchTerm(searchTerm: string) {
  state.currentSearchTerm = searchTerm;
}

export function setSelectedView(view: 'library' | 'favorites') {
  state.selectedView = view;
  if (view === 'favorites') {
    state.currentCategory = 'Favorites';
    state.currentSubcategory = 'All Favorites';
    state.currentSearchTerm = '';
    fetchFavorites(false);
  }
}
